<script setup>
// Paleta de blocos: clique adiciona depois do bloco selecionado. Lista de
// camadas embaixo (selecionar, subir/descer, duplicar, remover).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { FIXED_KINDS } from './useEmailBuilder';

const props = defineProps({
  catalog: { type: Object, default: null },
  blocks: { type: Array, default: () => [] },
  selectedId: { type: String, default: null },
  // Modo Pessoal: só a lista (assinatura/rodapé), sem paleta.
  palette: { type: Boolean, default: true },
});
const emit = defineEmits(['add', 'select', 'move', 'duplicate', 'remove']);
const { t } = useI18n();

const ICONS = {
  header: 'i-lucide-layout-template',
  hero: 'i-lucide-image',
  text: 'i-lucide-type',
  button: 'i-lucide-mouse-pointer-click',
  columns: 'i-lucide-columns-2',
  offer: 'i-lucide-shopping-bag',
  testimonial: 'i-lucide-quote',
  benefits: 'i-lucide-list-checks',
  event: 'i-lucide-calendar-clock',
  signature: 'i-lucide-pen-line',
  divider: 'i-lucide-minus',
  footer: 'i-lucide-shield-check',
};
const STRUCTURE = ['header', 'hero', 'text', 'columns', 'divider', 'footer'];
const CONTENT = [
  'button',
  'offer',
  'testimonial',
  'benefits',
  'signature',
  'event',
];

const kinds = computed(() => Object.keys(props.catalog?.variants || {}));
const label = kind => t(`EMAIL_TEMPLATES.BUILDER.BLOCKS.${kind}`);
const available = list => list.filter(k => kinds.value.includes(k));
// Cabeçalho e rodapé: só um de cada.
const disabled = kind =>
  ['header', 'footer'].includes(kind) &&
  props.blocks.some(b => b.kind === kind);
</script>

<template>
  <div class="flex flex-col gap-4 p-3">
    <div v-for="group in palette ? ['STRUCTURE', 'CONTENT'] : []" :key="group">
      <p
        class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
      >
        {{ t(`EMAIL_TEMPLATES.BUILDER.GROUPS.${group}`) }}
      </p>
      <div class="grid grid-cols-3 gap-1.5">
        <button
          v-for="kind in available(group === 'STRUCTURE' ? STRUCTURE : CONTENT)"
          :key="kind"
          type="button"
          :disabled="disabled(kind)"
          class="flex flex-col items-center gap-1 rounded-lg border border-n-weak bg-n-solid-1 px-1 py-2 text-[10px] text-n-slate-11 hover:border-n-brand hover:text-n-brand disabled:cursor-not-allowed disabled:opacity-40"
          @click="emit('add', kind)"
        >
          <span :class="ICONS[kind]" class="text-base" />
          {{ label(kind) }}
        </button>
      </div>
    </div>

    <div>
      <p
        class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
      >
        {{ t('EMAIL_TEMPLATES.BUILDER.GROUPS.LAYERS') }}
      </p>
      <ul class="flex flex-col gap-1">
        <li
          v-for="(blk, i) in blocks"
          :key="blk.id"
          class="group flex items-center gap-2 rounded-lg border px-2 py-1.5 text-xs"
          :class="
            blk.id === selectedId
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-transparent text-n-slate-11 hover:bg-n-solid-2'
          "
        >
          <button
            type="button"
            class="flex min-w-0 flex-1 items-center gap-2 text-left"
            @click="emit('select', blk.id)"
          >
            <span :class="ICONS[blk.kind]" class="shrink-0 text-n-slate-10" />
            <span class="truncate">{{
              blk.headline || blk.label || label(blk.kind)
            }}</span>
          </button>
          <span class="hidden shrink-0 items-center gap-0.5 group-hover:flex">
            <button
              type="button"
              class="i-lucide-chevron-up text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
              :disabled="i === 0"
              :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.UP')"
              @click="emit('move', blk.id, -1)"
            />
            <button
              type="button"
              class="i-lucide-chevron-down text-n-slate-10 hover:text-n-slate-12 disabled:opacity-30"
              :disabled="i === blocks.length - 1"
              :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.DOWN')"
              @click="emit('move', blk.id, 1)"
            />
            <button
              v-if="!FIXED_KINDS.includes(blk.kind)"
              type="button"
              class="i-lucide-copy text-n-slate-10 hover:text-n-slate-12"
              :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.DUPLICATE_BLOCK')"
              @click="emit('duplicate', blk.id)"
            />
            <button
              v-if="blk.kind !== 'footer'"
              type="button"
              class="i-lucide-trash-2 text-n-slate-10 hover:text-n-ruby-9"
              :title="t('EMAIL_TEMPLATES.BUILDER.ACTIONS.REMOVE_BLOCK')"
              @click="emit('remove', blk.id)"
            />
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
